import { useQueries } from '@tanstack/react-query';
import { getSpecificPortfolioV3, type Portfolio } from 'venn-api';
import { useMemo } from 'react';
import { combinePortfolios } from 'venn-utils';
import type { CombinedPortfoliosState, CombinePortfoliosDraftSettings, PortfolioInformation } from '../types';

export const useCombinedPortfolios = (
  portfolioInformation: PortfolioInformation[],
  combineSettings: CombinePortfoliosDraftSettings,
): CombinedPortfoliosState => {
  const results = useQueries<Portfolio[]>({
    queries: portfolioInformation.map(({ id }) => ({
      queryKey: ['portfolio', id],
      queryFn: (): Promise<Portfolio> => getSpecificPortfolioV3(id).then((res) => res.content),
    })),
  });

  return useMemo(() => {
    const { structureOption, organizeOption, combinedPortfolioName, includeHousehold, includeClient } = combineSettings;
    if (results.some((result) => result.isLoading) || structureOption === null) {
      return {
        portfoliosLoading: true,
        portfolios: null,
      };
    }

    const portfolios = results.map((res) => res.data as Portfolio);

    return {
      portfolios: combinePortfolios(
        portfolios,
        portfolioInformation.map(({ id, householdName, clientName }) => ({
          id,
          householdName: householdName ?? undefined,
          clientName: clientName ?? undefined,
        })),
        {
          structureOption,
          organizeOption: organizeOption ?? undefined,
          combinedPortfolioName,
          includeHousehold,
          includeClient,
        },
      ),
      portfoliosLoading: false,
    };
  }, [results, portfolioInformation, combineSettings]);
};
